import { useEffect, useState } from "react";
import { api, errDetail, fmtEur } from "../lib/api";
import { toast } from "sonner";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { PieChart, TrendingUp, AlertCircle } from "lucide-react";

const Kpi = ({ label, value, sub, testid, tone }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid={testid}>
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`text-xl font-bold ${tone || "text-[#1B2A4A]"}`}>{value}</div>
    {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
  </div>
);

const Bar = ({ pct, color }) => (
  <div className="h-2 rounded-full bg-gray-100 overflow-hidden"><div className={`h-full ${color}`} style={{ width: `${Math.min(100, pct)}%` }} /></div>
);

export default function SpendAnalytics() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/srm/spend/summary").then((r) => setS(r.data)).catch((e) => toast.error(errDetail(e))); }, []);
  if (!s) return <div className="p-6 text-gray-400">Lädt…</div>;
  const maxCat = Math.max(1, ...s.by_category.map((c) => c.amount));
  const maxMonth = Math.max(1, ...s.by_month.map((m) => m.amount));
  return (
    <div className="p-6 max-w-[1300px] mx-auto" data-testid="spend-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-11 w-11 rounded-xl bg-[#1B2A4A] flex items-center justify-center"><PieChart className="h-5 w-5 text-[#F5A623]" /></div>
        <div><h1 className="text-2xl font-bold text-[#1B2A4A]">Spend-Analyse</h1><p className="text-sm text-gray-500">Cube · Konzentration · Tail-Spend · Maverick</p></div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Kpi label="Gesamt-Spend" value={fmtEur(s.total)} testid="kpi-total" />
        <Kpi label="Top-5-Konzentration" value={`${s.concentration.top5_share_pct}%`} sub={`${s.concentration.supplier_count} Lieferanten`} testid="kpi-concentration" />
        <Kpi label="Adressierbar" value={fmtEur(s.addressable.amount)} sub={`${s.addressable.pct}%`} testid="kpi-addressable" />
        <Kpi label="Maverick" value={fmtEur(s.maverick.amount)} sub={`${s.maverick.pct}% ohne PO`} tone="text-red-600" testid="kpi-maverick" />
        <Kpi label="Off-Contract" value={fmtEur(s.off_contract.amount)} sub={`${s.off_contract.pct}%`} tone="text-amber-600" testid="kpi-offcontract" />
      </div>

      {s.review_queue_count > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 mb-6 flex items-center gap-2" data-testid="review-banner">
          <AlertCircle className="h-4 w-4" /> {s.review_queue_count} unklassifizierte Buchungen (Confidence &lt; 0,85) — aus publizierten Summen ausgeschlossen.
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="font-semibold text-[#1B2A4A] mb-3">Kategorie-Split</h3>
          <div className="space-y-2" data-testid="category-split">
            {s.by_category.map((c) => (
              <div key={c.category}><div className="flex justify-between text-sm mb-1"><span>{c.category}</span><span className="text-gray-500">{fmtEur(c.amount)}</span></div><Bar pct={(c.amount / maxCat) * 100} color="bg-[#1B2A4A]" /></div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="font-semibold text-[#1B2A4A] mb-3 flex items-center gap-1"><TrendingUp className="h-4 w-4" /> Monatstrend</h3>
          <div className="space-y-2" data-testid="month-trend">
            {s.by_month.map((m) => (
              <div key={m.period}><div className="flex justify-between text-sm mb-1"><span>{m.period}</span><span className="text-gray-500">{fmtEur(m.amount)}</span></div><Bar pct={(m.amount / maxMonth) * 100} color="bg-[#F5A623]" /></div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mt-6">
        <h3 className="font-semibold text-[#1B2A4A] px-4 py-3 border-b border-gray-100">Top-Lieferanten</h3>
        <Table><TableHeader><TableRow><TableHead>Lieferant</TableHead><TableHead className="text-right">Spend</TableHead><TableHead className="text-right">Anteil</TableHead></TableRow></TableHeader>
          <TableBody>{s.by_supplier.map((r) => (
            <TableRow key={r.supplier_id} data-testid={`spend-supplier-${r.supplier_id}`}><TableCell>{r.supplier_name}</TableCell>
              <TableCell className="text-right">{fmtEur(r.amount)}</TableCell><TableCell className="text-right text-gray-500">{s.total ? ((r.amount / s.total) * 100).toFixed(1) : 0}%</TableCell></TableRow>
          ))}</TableBody></Table>
      </div>
    </div>
  );
}
